import { useEffect, useState } from "react";

export const THEME_STORAGE_KEY = "long-recorder-theme";

export type Theme = "light" | "dark";

export function parseTheme(value: string | null | undefined): Theme | undefined {
  return value === "light" || value === "dark" ? value : undefined;
}

export function resolveTheme(
  storedValue: string | null | undefined,
  systemPrefersDark: boolean,
): Theme {
  return parseTheme(storedValue) ?? (systemPrefersDark ? "dark" : "light");
}

function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";

  let storedValue: string | null = null;
  try {
    storedValue = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    // Theme selection is a convenience only; private browsing can deny storage.
  }

  return resolveTheme(
    storedValue,
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );
}

export function useTheme(): readonly [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Keep the selected theme for this page even if persistent storage fails.
    }
  }, [theme]);

  return [theme, setTheme] as const;
}

export function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const nextTheme = theme === "dark" ? "light" : "dark";

  return (
    <button
      className="theme-toggle"
      type="button"
      aria-label={`Switch to ${nextTheme} mode`}
      aria-pressed={theme === "dark"}
      onClick={() => setTheme(nextTheme)}
    >
      <span aria-hidden="true">{theme === "dark" ? "☀" : "◐"}</span>
      {theme === "dark" ? "Light" : "Dark"}
    </button>
  );
}
